import { api } from './api';

export interface AppNotification {
  id: string;
  userId: string;
  title: string;
  message: string;
  type: string; // 'info' | 'success' | 'warning' | 'error'
  category?: string; // 'comment' | 'gamification' | 'system'
  actionUrl?: string;
  read: boolean;
  readAt?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export type NotificationAudience = 'all' | 'course' | 'access_group' | 'users';
export interface NotificationDraft {
  title: string;
  message: string;
  actionUrl: string | null;
  audienceType: NotificationAudience;
  targetId: string | null;
  userIds: string[];
}
export interface NotificationCampaign extends NotificationDraft {
  id: string;
  selectedUserCount: number;
  status: 'draft' | 'sent';
  revision: number;
  recipientCount: number;
  sentAt: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface NotificationPreview {
  recipientCount: number;
  previewHash: string;
  revision: number;
}
interface CampaignPage {
  data: NotificationCampaign[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

class NotificationsService {
  async campaigns(status: NotificationCampaign['status'], page = 1): Promise<CampaignPage> {
    const { data } = await api.get(`/notifications/campaigns?page=${page}&limit=20&status=${status}`);
    return Array.isArray(data.data) ? data : data.data;
  }

  async campaign(id: string): Promise<NotificationCampaign> {
    const { data } = await api.get(`/notifications/campaigns/${id}`);
    return data.data ?? data;
  }

  async createCampaign(draft: NotificationDraft): Promise<NotificationCampaign> {
    const { data } = await api.post('/notifications/campaigns', draft, { retryOnNetworkError: false });
    return data.data ?? data;
  }

  async updateCampaign(id: string, draft: NotificationDraft, revision: number): Promise<NotificationCampaign> {
    const { data } = await api.patch(`/notifications/campaigns/${id}`, { ...draft, revision }, { retryOnNetworkError: false });
    return data.data ?? data;
  }

  async previewCampaign(id: string): Promise<NotificationPreview> {
    const { data } = await api.post(`/notifications/campaigns/${id}/preview`, {}, { retryOnNetworkError: false });
    return data.data ?? data;
  }

  async sendCampaign(id: string, previewHash: string): Promise<{ campaign: NotificationCampaign; alreadySent: boolean }> {
    const { data } = await api.post(`/notifications/campaigns/${id}/send`, { previewHash, confirmSend: true }, { retryOnNetworkError: false });
    return data.data ?? data;
  }

  async getAll(
    page = 1,
    limit = 20,
  ): Promise<{
    data: AppNotification[];
    meta: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const { data } = await api.get(
      `/notifications?page=${page}&limit=${limit}`,
    );
    const unwrapped = data?.data || data;
    return {
      data: Array.isArray(unwrapped) ? unwrapped : unwrapped?.data || [],
      meta: data?.meta ||
        unwrapped?.meta || {
          page,
          limit,
          total: 0,
          totalPages: 1,
        },
    };
  }

  async getUnreadCount(): Promise<number> {
    const { data } = await api.get('/notifications/unread-count');
    const unwrapped = data?.data || data;
    return unwrapped?.count ?? 0;
  }

  async markAsRead(id: string): Promise<AppNotification> {
    const { data } = await api.patch(`/notifications/${id}/read`);
    return data?.data || data;
  }

  async markAllAsRead(): Promise<void> {
    await api.patch('/notifications/read-all');
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/notifications/${id}`);
  }
}

export const notificationsService = new NotificationsService();
